import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaArrowLeft, FaEdit, FaTrashAlt, FaExternalLinkAlt } from "react-icons/fa";
import { useLocation, useNavigate } from "react-router-dom";
import "../assets/css/ViewCustomer.css";
import "../assets/css/DigixLeads.css";
import axiosInstance from "../components/api/axiosInstance";
import AddFollowup from "./AddFollowup";
import { resolveImageUrl } from "../config/apiConfig";
import { SupportSortableHeader } from "./SupportSortableHeader";
import { useResizableColumns } from "../hooks/useResizableColumns";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../components/TablePagination";
import { getAuthItem } from "../utils/authStorage";
import { formatFullName, formatPascal, formatPhone } from "../utils/formatters";
import { useConfirm } from "../components/ConfirmModal";
import AssignedToSelect from "../components/AssignedToSelect";
import { portalTypeLabel } from "../utils/loginDomain";
import MapLink from "../components/MapLink";
import TenantInvoicesTab from "./TenantInvoicesTab";
import { ResultsMobileCard, ResultsMobileList } from "../components/ResultsMobile";

const LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY = "leadFollowupsRowsPerPage";
const LEAD_ATTACHMENTS_ROWS_PER_PAGE_KEY = "leadAttachmentsRowsPerPage";
const CUSTOMER_SOCIAL_ACCOUNTS_ROWS_PER_PAGE_KEY = "customerSocialAccountsRowsPerPage";
const CUSTOMER_PROJECTS_ROWS_PER_PAGE_KEY = "customerProjectsRowsPerPage";

const fixFileUrl = (url) => {
  if (!url) return url;
  return resolveImageUrl(url);
};

const getStatusTone = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (/(won|qualified|converted|completed|active)/.test(value)) return "success";
  if (/(lost|rejected|cancelled|canceled|closed)/.test(value)) return "danger";
  if (/(contacted|follow|pending|progress|warm)/.test(value)) return "warning";
  if (/(new|open|cold)/.test(value)) return "info";
  return "neutral";
};

export function ViewLead({ leadId, onBack, onEditLead, onAddFollowup, type = "lead", onPageTitleReady }) {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const [leadData, setLeadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [followups, setFollowups] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loadingDocumentTypes, setLoadingDocumentTypes] = useState(false);
  const [domains, setDomains] = useState([]);
  const [followupSearch, setFollowupSearch] = useState("");
  const [followupDate, setFollowupDate] = useState("");
  const [attachmentSearch, setAttachmentSearch] = useState("");
  const [followupSortColumn, setFollowupSortColumn] = useState("nextFollowupDate");
  const [followupSortDirection, setFollowupSortDirection] = useState("desc");
  const [followupPage, setFollowupPage] = useState(1);
  const [followupRowsPerPage, setFollowupRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [attachmentSortColumn, setAttachmentSortColumn] = useState(null);
  const [attachmentSortDirection, setAttachmentSortDirection] = useState("asc");
  const [attachmentPage, setAttachmentPage] = useState(1);
  const [detailTab, setDetailTab] = useState("company");
  const [attachmentRowsPerPage, setAttachmentRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(LEAD_ATTACHMENTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [attachmentForm, setAttachmentForm] = useState({
    file: null,
    documentTypeId: "",
    remarks: ""
  });
  const [editingFollowup, setEditingFollowup] = useState(null);
  const [followupEditForm, setFollowupEditForm] = useState({
    followupDate: "",
    followupType: "",
    remarks: "",
    nextFollowupDate: ""
  });
  const [updatingFollowup, setUpdatingFollowup] = useState(false);
  const [assignUsers, setAssignUsers] = useState([]);
  const [savingAssigned, setSavingAssigned] = useState(false);
  const [showAddFollowupModal, setShowAddFollowupModal] = useState(false);
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [socialSortColumn, setSocialSortColumn] = useState(null);
  const [socialSortDirection, setSocialSortDirection] = useState("asc");
  const [socialAccountPage, setSocialAccountPage] = useState(1);
  const [socialAccountRowsPerPage, setSocialAccountRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMER_SOCIAL_ACCOUNTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [customerProjects, setCustomerProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [projectSortColumn, setProjectSortColumn] = useState(null);
  const [projectSortDirection, setProjectSortDirection] = useState("asc");
  const [projectPage, setProjectPage] = useState(1);
  const [projectRowsPerPage, setProjectRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(CUSTOMER_PROJECTS_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [customerProjectsStatusPreset, setCustomerProjectsStatusPreset] = useState("all");
  const [socialAccountsColumnWidths, setSocialAccountsColumnWidths] = useState({});
  const socialAccountsResizeRef = useRef({ startX: 0, startWidth: 0, columnIndex: null });
  const followupColDefaultWidths = [170, 130, 260, 170, 170, 140];
  const attachmentColDefaultWidths = [250, 170, 220, 170, 140];
  const projectColDefaultWidths = [220, 130, 140, 130, 120, 130, 130, 120];
  const { getColumnStyle: getFollowupColStyle, handleResizeStart: handleFollowupResizeStart } =
    useResizableColumns(followupColDefaultWidths);
  const { getColumnStyle: getAttachmentColStyle, handleResizeStart: handleAttachmentResizeStart } =
    useResizableColumns(attachmentColDefaultWidths);
  const { getColumnStyle: getProjectColStyle, handleResizeStart: handleProjectResizeStart } =
    useResizableColumns(projectColDefaultWidths);

  // Load active users for Assigned To reassignment (leads only)
  useEffect(() => {
    if (type === "client") return undefined;
    let cancelled = false;
    const loadUsers = async () => {
      try {
        const res = await axiosInstance.get("/users", { params: { status: "Active" } });
        const raw = Array.isArray(res.data) ? res.data : res.data?.users || res.data?.data || [];
        if (!cancelled) setAssignUsers(raw);
      } catch (err) {
        console.error("Error loading users for Assigned To:", err);
        if (!cancelled) setAssignUsers([]);
      }
    };
    loadUsers();
    return () => {
      cancelled = true;
    };
  }, [type]);

  // Fetch lead/customer/training lead details
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      // Define variables based on type
      const isTrainingLead = type === "training-lead";
      const endpoint = type === "client" 
        ? `/client/${leadId}` 
        : isTrainingLead 
        ? `/training-lead/${leadId}` 
        : `/lead/${leadId}`;
      const dataKey = type === "client" 
        ? "client" 
        : isTrainingLead 
        ? "training_lead" 
        : "lead";
      const idField = type === "client" 
        ? "client_id" 
        : isTrainingLead 
        ? "Training_Lead_Id" 
        : "Lead_Id";
      const allEndpoint = type === "client" 
        ? "/tenants" 
        : isTrainingLead 
        ? "/training-leads" 
        : "/leads"; // API endpoint for business leads
      
      try {
        // Try single endpoint first
        const response = await axiosInstance.get(endpoint);
        if (response.data) {
          // Could be { lead/customer/training_lead: {...}, address: {...} } or direct object
          if (response.data[dataKey]) {
            setLeadData(response.data);
          } else {
            setLeadData({ [dataKey]: response.data, address: {} });
          }
        }
      } catch (error) {
        console.error(`Error fetching single ${type}:`, error);
        // Fallback: fetch all and find by ID
        try {
          const allResponse = await axiosInstance.get(allEndpoint);
          if (allResponse.data && Array.isArray(allResponse.data)) {
            const found = allResponse.data.find(item => {
              const data = item[dataKey] || item;
              const itemId = data[idField];
              // Handle both string and number comparisons
              return itemId === leadId || String(itemId) === String(leadId) || parseInt(itemId) === parseInt(leadId);
            });
            if (found) {
              if (found[dataKey]) {
                setLeadData(found);
              } else {
                setLeadData({ [dataKey]: found, address: {} });
              }
            }
          }
        } catch (fallbackError) {
          console.error(`Error fetching ${type}s fallback:`, fallbackError);
        }
      } finally {
        setLoading(false);
      }
    };

    if (leadId) {
      fetchDetails();
    }
  }, [leadId, type]);

  // Tenant view: page header = name + #ID + status
  useEffect(() => {
    if (type !== "client" || typeof onPageTitleReady !== "function") return undefined;

    if (loading || !leadData) {
      onPageTitleReady(null);
      return () => onPageTitleReady(null);
    }

    const client = leadData.client || {};
    const typeRaw = String(client.Customer_Type || client.customer_type || "").trim().toLowerCase();
    const isOrg =
      typeRaw === "organization" ||
      typeRaw === "organisation" ||
      typeRaw === "org";
    const company = String(client.Company_Name || client.company_name || "").trim();
    const person = formatFullName(null, client.First_Name, client.Last_Name);
    const personOk = person && person !== "—";
    const displayName = formatPascal(
      isOrg ? company || (personOk ? person : "") || "Client" : personOk ? person : company || "Client"
    );
    const id = client.client_id ?? client.Client_Id ?? leadId;
    const status = client.Status || client.status || "";
    const tone = getStatusTone(status);

    onPageTitleReady(
      <span className="page-title-with-meta">
        <span className="page-title-name">{displayName}</span>
        {id != null && id !== "" ? <span className="page-title-id">#{id}</span> : null}
        {status ? (
          <span className={`page-title-status viewlead-status-pill viewlead-status-pill--${tone}`}>
            {status}
          </span>
        ) : null}
      </span>
    );

    return () => onPageTitleReady(null);
  }, [type, loading, leadData, leadId, onPageTitleReady]);

  // Fetch domains for mapping Domain_Id to name (customers/business leads)
  useEffect(() => {
    const fetchDomains = async () => {
      try {
        const res = await axiosInstance.get("/domains");
        const raw = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];
        const mapped = raw
          .map((d) => ({
            id: d.Domain_Id ?? d.domain_id ?? d.id,
            name: d.Domain_Name ?? d.domain_name ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && d.name.trim() !== "");
        setDomains(mapped);
      } catch (err) {
        console.error("Error fetching domains in ViewLead:", err);
        setDomains([]);
      }
    };
    fetchDomains();
  }, []);

  // Fetch followups function
  const fetchFollowups = async () => {
    if (!leadId) return;
    try {
      const isTrainingLead = type === "training-lead";
      let endpoint;
      
      if (type === "client") {
        endpoint = `/followups?client_id=${leadId}`;
      } else if (isTrainingLead) {
        // Use training-lead-specific endpoint with path parameter
        endpoint = `/training-followups/${leadId}`;
      } else {
        endpoint = `/followups?lead_id=${leadId}`;
      }
      
      const response = await axiosInstance.get(endpoint);
      if (response.data && Array.isArray(response.data)) {
        setFollowups(response.data);
      } else {
        setFollowups([]);
      }
    } catch (error) {
      // For training leads, don't fallback to lead_id to avoid mixing followups
      if (type === "training-lead") {
        console.error("Error fetching training lead followups:", error);
        setFollowups([]);
        return;
      }
      // Followups endpoint may not exist yet
      console.error("Error fetching followups:", error);
      setFollowups([]);
    }
  };

  // Fetch followups on mount and when leadId/type changes
  useEffect(() => {
    fetchFollowups();
  }, [leadId, type]);

  const [ownerBusinesses, setOwnerBusinesses] = useState([]);
  const [isPrimaryCompany, setIsPrimaryCompany] = useState(false);
  const [settingPrimary, setSettingPrimary] = useState(false);
  const [ownerBizReload, setOwnerBizReload] = useState(0);
  // Set when this company signs in with its own login linked to the owner's main login.
  const [companyLogin, setCompanyLogin] = useState(null);
  useEffect(() => {
    if (!leadId || type !== "client") {
      setOwnerBusinesses([]);
      setIsPrimaryCompany(false);
      setCompanyLogin(null);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get(`/client/${leadId}/owner-businesses`)
      .then((res) => {
        if (cancelled) return;
        setOwnerBusinesses(Array.isArray(res.data?.businesses) ? res.data.businesses : []);
        setIsPrimaryCompany(Boolean(res.data?.Is_Primary_Company));
        setCompanyLogin(
          res.data?.Company_Login
            ? { login: res.data.Login_Email, mainLogin: res.data.owner?.Company_Email }
            : null
        );
      })
      .catch(() => {
        if (!cancelled) setOwnerBusinesses([]);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, type, ownerBizReload]);

  const handleMakePrimary = async () => {
    const ok = await confirm({
      title: "Make Primary Company",
      message: "Make this the owner's primary company? Their other companies will no longer be primary.",
    });
    if (!ok) return;
    setSettingPrimary(true);
    try {
      await axiosInstance.post(`/client/${leadId}/set-primary`);
      setOwnerBizReload((n) => n + 1);
    } catch (err) {
      window.alert(err.response?.data?.error || "Could not update the primary company.");
    } finally {
      setSettingPrimary(false);
    }
  };

  // Fetch customer accounts when viewing a customer
  const fetchCustomerAccounts = async () => {
    if (!leadId || type !== "client") {
      setCustomerAccounts([]);
      return;
    }
    setLoadingAccounts(true);
    try {
      const response = await axiosInstance.get(`/social-accounts/client/${leadId}`);
      if (response.data && response.data.accounts && Array.isArray(response.data.accounts)) {
        const mappedAccounts = response.data.accounts.map(account => {
          return {
            id: account.Account_Id || account.account_id,
            account_id: account.Account_Id || account.account_id,
            accountName: account.Account_Name || account.account_name || "—",
            username: account.Username || account.username || "—",
            password: account.Password || account.password || "—",
            profileUrl: account.Profile_URL || account.profile_url || "—",
            contact: account.Contact_Number || account.contact_number || "—",
            emailUsed: account.Email_Used || account.email_used || "—",
            platform: account.Platform_Name || account.platform_name || "—",
            status: account.Status || account.status || "Inactive",
            notes: account.Notes || account.notes || "—"
          };
        });
        setCustomerAccounts(mappedAccounts);
      } else {
        setCustomerAccounts([]);
      }
    } catch (error) {
      console.error("Error fetching customer accounts:", error);
      setCustomerAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Fetch customer accounts on mount and when leadId/type changes
  useEffect(() => {
    if (type === "client") {
      fetchCustomerAccounts();
    } else {
      setCustomerAccounts([]);
    }
  }, [leadId, type]);

  const getSocialSortValue = (account, columnId) => {
    switch (columnId) {
      case "accountName":
        return String(account.accountName || "").toLowerCase();
      case "username":
        return String(account.username || "").toLowerCase();
      case "password":
        return String(account.password || "").toLowerCase();
      case "platform":
        return String(account.platform || "").toLowerCase();
      case "profileUrl":
        return String(account.profileUrl || "").toLowerCase();
      case "contact":
        return String(account.contact || "").toLowerCase();
      case "emailUsed":
        return String(account.emailUsed || "").toLowerCase();
      case "status":
        return String(account.status || "").toLowerCase();
      case "notes":
        return String(account.notes || "").toLowerCase();
      default:
        return "";
    }
  };

  const sortedCustomerAccounts = useMemo(() => {
    if (!socialSortColumn) return customerAccounts;
    const mult = socialSortDirection === "desc" ? -1 : 1;
    return [...customerAccounts].sort((a, b) => {
      const va = getSocialSortValue(a, socialSortColumn);
      const vb = getSocialSortValue(b, socialSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a.id || a.account_id || 0);
      const idb = Number(b.id || b.account_id || 0);
      return (ida - idb) * mult;
    });
  }, [customerAccounts, socialSortColumn, socialSortDirection]);

  const socialAccountTotalPages = Math.max(1, Math.ceil(sortedCustomerAccounts.length / socialAccountRowsPerPage));
  const socialAccountStartIdx = (socialAccountPage - 1) * socialAccountRowsPerPage;
  const currentSocialAccounts = sortedCustomerAccounts.slice(socialAccountStartIdx, socialAccountStartIdx + socialAccountRowsPerPage);

  useEffect(() => {
    if (socialAccountPage > socialAccountTotalPages && socialAccountTotalPages > 0) {
      setSocialAccountPage(1);
    }
  }, [socialAccountPage, socialAccountTotalPages, sortedCustomerAccounts.length]);

  const handleSocialAccountRowsPerPageChange = (next) => {
    setSocialAccountRowsPerPage(next);
    setSocialAccountPage(1);
    localStorage.setItem(CUSTOMER_SOCIAL_ACCOUNTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleSocialSort = (columnId) => {
    setSocialAccountPage(1);
    if (socialSortColumn === columnId) {
      setSocialSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSocialSortColumn(columnId);
      setSocialSortDirection("asc");
    }
  };

  const socialAccountsDefaultWidths = [180, 130, 140, 120, 200, 130, 180, 110, 220, 130];
  const getSocialAccountsColumnWidth = (columnIndex) => {
    if (socialAccountsColumnWidths[columnIndex]) {
      return `${socialAccountsColumnWidths[columnIndex]}px`;
    }
    return `${socialAccountsDefaultWidths[columnIndex] || 120}px`;
  };

  const getSocialAccountsColumnMinWidth = (columnIndex) => {
    if (socialAccountsColumnWidths[columnIndex]) {
      return `${socialAccountsColumnWidths[columnIndex]}px`;
    }
    return `${socialAccountsDefaultWidths[columnIndex] || 120}px`;
  };

  const handleSocialAccountsResizeStart = (e, columnIndex) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.currentTarget.parentElement;
    if (!th) return;

    socialAccountsResizeRef.current = {
      startX: e.clientX,
      startWidth: th.offsetWidth,
      columnIndex
    };

    document.body.classList.add("resizing");

    const handleMouseMove = (moveEvent) => {
      if (socialAccountsResizeRef.current.columnIndex === null) return;
      const diff = moveEvent.clientX - socialAccountsResizeRef.current.startX;
      const newWidth = Math.max(90, socialAccountsResizeRef.current.startWidth + diff);
      setSocialAccountsColumnWidths((prev) => ({
        ...prev,
        [socialAccountsResizeRef.current.columnIndex]: newWidth
      }));
    };

    const handleMouseUp = () => {
      socialAccountsResizeRef.current = { startX: 0, startWidth: 0, columnIndex: null };
      document.body.classList.remove("resizing");
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const handleDeleteCustomerSocialAccount = async (accountId) => {
    if (!accountId) return;
    if (!(await confirm({ title: "Delete Social Account", message: "Are you sure you want to delete this social account?" }))) return;

    try {
      await axiosInstance.delete(`/social-account/${accountId}`);
      await fetchCustomerAccounts();
    } catch (error) {
      console.error("Error deleting social account:", error);
      alert(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to delete social account."
      );
    }
  };

  // Fetch projects for this customer
  const fetchCustomerProjects = async () => {
    if (!leadId || type !== "client") {
      setCustomerProjects([]);
      return;
    }
    setLoadingProjects(true);
    try {
      const response = await axiosInstance.get(`/projects/client/${leadId}`);
      const raw = response.data;
      const arr = Array.isArray(raw)
        ? raw
        : Array.isArray(raw?.projects)
          ? raw.projects
          : Array.isArray(raw?.data)
            ? raw.data
            : [];
      const mapped = (arr || [])
        .map((p) => {
          const id = p.Project_Id || p.project_id || p.id;
          if (!id) return null;
          return {
            id,
            projectName: p.Project_Name || p.project_name || "—",
            shortCode: p.Short_Code || p.short_code || "—",
            domain: p.Domain || p.domain || "—",
            category: p.Category || p.category || "—",
            status: p.Status || p.status || "—",
            startDate: p.Start_Date || p.start_date || "",
            endDate: p.End_Date || p.end_date || "",
            visibility: p.Visibility || p.visibility || "—"
          };
        })
        .filter(Boolean);
      setCustomerProjects(mapped);
    } catch (error) {
      console.error("Error fetching customer projects:", error);
      setCustomerProjects([]);
    } finally {
      setLoadingProjects(false);
    }
  };

  // Fetch customer projects on mount and when leadId/type changes
  useEffect(() => {
    if (type === "client") {
      fetchCustomerProjects();
    } else {
      setCustomerProjects([]);
    }
  }, [leadId, type]);

  useEffect(() => {
    const preset = location.state?.customerProjectsStatusPreset;
    if (typeof preset === "string" && preset.trim()) {
      setCustomerProjectsStatusPreset(preset);
    } else {
      setCustomerProjectsStatusPreset("all");
    }
  }, [location.state]);

  const filteredCustomerProjects = (customerProjects || []).filter((project) => {
    if (customerProjectsStatusPreset === "all") return true;
    const status = String(project?.status || "").trim().toLowerCase();
    const compact = status.replace(/[\s_-]+/g, "");
    if (customerProjectsStatusPreset === "active") {
      if (!status) return true;
      return status !== "inactive" && status !== "deactivated" && status !== "closed";
    }
    if (customerProjectsStatusPreset === "new") {
      return status.includes("new");
    }
    if (customerProjectsStatusPreset === "inProgress") {
      return (
        status.includes("in progress") ||
        status.includes("in-progress") ||
        status.includes("in_progress") ||
        compact.includes("inprogress")
      );
    }
    return true;
  });

  const getProjectSortValue = (project, columnId) => {
    switch (columnId) {
      case "projectName":
        return String(project.projectName || "").toLowerCase();
      case "shortCode":
        return String(project.shortCode || "").toLowerCase();
      case "domain":
        return String(project.domain || "").toLowerCase();
      case "category":
        return String(project.category || "").toLowerCase();
      case "status":
        return String(project.status || "").toLowerCase();
      case "startDate": {
        const t = new Date(project.startDate || "").getTime();
        return Number.isFinite(t) ? t : 0;
      }
      case "endDate": {
        const t = new Date(project.endDate || "").getTime();
        return Number.isFinite(t) ? t : 0;
      }
      case "visibility":
        return String(project.visibility || "").toLowerCase();
      default:
        return "";
    }
  };

  const sortedCustomerProjects = useMemo(() => {
    if (!projectSortColumn) return filteredCustomerProjects;
    const mult = projectSortDirection === "desc" ? -1 : 1;
    return [...filteredCustomerProjects].sort((a, b) => {
      const va = getProjectSortValue(a, projectSortColumn);
      const vb = getProjectSortValue(b, projectSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return Number(a.id || 0) - Number(b.id || 0);
    });
  }, [filteredCustomerProjects, projectSortColumn, projectSortDirection]);

  const projectTotalPages = Math.max(1, Math.ceil(sortedCustomerProjects.length / projectRowsPerPage));
  const projectStartIdx = (projectPage - 1) * projectRowsPerPage;
  const currentCustomerProjects = sortedCustomerProjects.slice(projectStartIdx, projectStartIdx + projectRowsPerPage);

  useEffect(() => {
    if (projectPage > projectTotalPages && projectTotalPages > 0) {
      setProjectPage(1);
    }
  }, [projectPage, projectTotalPages, sortedCustomerProjects.length]);

  useEffect(() => {
    setProjectPage(1);
  }, [customerProjectsStatusPreset]);

  const handleProjectRowsPerPageChange = (next) => {
    setProjectRowsPerPage(next);
    setProjectPage(1);
    localStorage.setItem(CUSTOMER_PROJECTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleProjectSort = (columnId) => {
    setProjectPage(1);
    if (projectSortColumn === columnId) {
      setProjectSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setProjectSortColumn(columnId);
      setProjectSortDirection("asc");
    }
  };

  // Convert API datetime to datetime-local value (YYYY-MM-DDTHH:mm)
  const toDateTimeLocal = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return "";
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      const hours = String(d.getHours()).padStart(2, "0");
      const minutes = String(d.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    } catch {
      return "";
    }
  };

  const handleEditFollowup = (f) => {
    setEditingFollowup(f);
    setFollowupEditForm({
      followupDate: toDateTimeLocal(f.Followup_Date || f.followup_date),
      followupType: f.Followup_Type || f.Type || f.followup_type || "",
      remarks: f.Remarks || f.remarks || "",
      nextFollowupDate: toDateTimeLocal(f.Next_Followup_Date || f.next_followup_date)
    });
  };

  const handleCloseEditFollowup = () => {
    setEditingFollowup(null);
    setFollowupEditForm({ followupDate: "", followupType: "", remarks: "", nextFollowupDate: "" });
  };

  const handleFollowupEditChange = (e) => {
    const { name, value } = e.target;
    setFollowupEditForm(prev => ({ ...prev, [name]: value }));
  };

  const handleUpdateFollowupSubmit = async (e) => {
    e.preventDefault();
    if (!editingFollowup) return;
    const followupId = editingFollowup.Followup_Id || editingFollowup.followup_id || editingFollowup.id;
    if (!followupId) return;

    // API expects YYYY-MM-DDTHH:mm:ss
    const formattedDate = followupEditForm.followupDate ? followupEditForm.followupDate + ":00" : null;
    const formattedNext = followupEditForm.nextFollowupDate ? followupEditForm.nextFollowupDate + ":00" : null;

    let updatedBy = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        updatedBy = user.user_id || user.id || 1;
      }
    } catch (err) {
      console.error("Error reading user for Updated_By:", err);
    }

    setUpdatingFollowup(true);
    try {
      if (type === "training-lead") {
        await axiosInstance.put(`/update-training-followup/${followupId}`, {
          Training_Lead_Id: leadId,
          Followup_Date: formattedDate,
          Followup_Type: followupEditForm.followupType || null,
          Remarks: followupEditForm.remarks || null,
          Next_Followup_Date: formattedNext,
          Updated_By: updatedBy
        });
      } else {
        await axiosInstance.put(`/update-followup/${followupId}`, {
          Lead_Id: type === "lead" ? leadId : null,
          client_id: type === "client" ? leadId : null,
          Followup_Date: formattedDate,
          Followup_Type: followupEditForm.followupType || null,
          Remarks: followupEditForm.remarks || null,
          Next_Followup_Date: formattedNext,
          Updated_By: updatedBy
        });
      }
      alert("Followup updated successfully.");
      handleCloseEditFollowup();
      fetchFollowups();
    } catch (error) {
      console.error("Error updating followup:", error);
      const msg = error.response?.data?.message || error.response?.data?.error || "Failed to update followup.";
      alert(msg);
    } finally {
      setUpdatingFollowup(false);
    }
  };

  const handleCloseAddFollowupModal = () => {
    setShowAddFollowupModal(false);
    fetchFollowups();
  };

  // Delete followup - use separate endpoints for training vs business leads
  const handleDeleteFollowup = async (followupId) => {
    if (!(await confirm({ title: "Delete Follow-up", message: "Are you sure you want to delete this followup?" }))) {
      return;
    }

    try {
      // Use training-followup endpoint for training leads, regular followup endpoint for business leads and customers
      const deleteEndpoint = type === "training-lead" 
        ? `/training-followup/${followupId}` 
        : `/followup/${followupId}`;
      
      const response = await axiosInstance.delete(deleteEndpoint);
      if (response.data && response.data.message) {
        alert(response.data.message);
        // Refresh followups list
        fetchFollowups();
      }
    } catch (error) {
      console.error("Error deleting followup:", error);
      const errorMsg = error.response?.data?.message || error.response?.data?.error || "Failed to delete followup.";
      alert(errorMsg);
    }
  };

  // Fetch attachments (skip for training leads)
  const fetchAttachments = async () => {
    if (!leadId) return;
    // Don't fetch attachments for training leads
    if (type === "training-lead") {
      setAttachments([]);
      return;
    }
    try {
      // Use /attachments endpoint with query parameter based on type
      const param = type === "client" ? `client_id=${leadId}` : `lead_id=${leadId}`;
      const response = await axiosInstance.get(`/attachments?${param}`);
      if (response.data && Array.isArray(response.data)) {
        setAttachments(response.data);
      } else {
        setAttachments([]);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      // Set empty array if attachments can't be fetched
      setAttachments([]);
    }
  };

  useEffect(() => {
    fetchAttachments();
  }, [leadId, type]);

  // Fetch document types for attachments (Business Leads + Customers)
  useEffect(() => {
    const fetchDocumentTypes = async () => {
      if (type === "training-lead") {
        setDocumentTypes([]);
        return;
      }
      setLoadingDocumentTypes(true);
      try {
        const response = await axiosInstance.get("/document-types");
        const raw = response?.data;
        const arr = Array.isArray(raw) ? raw : raw?.data || [];
        const mapped = (arr || [])
          .map((d) => ({
            id: d.Document_Type_Id ?? d.document_type_id ?? d.id,
            name: d.Document_Type ?? d.document_type ?? d.name ?? ""
          }))
          .filter((d) => d.id != null && String(d.name).trim() !== "");
        mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setDocumentTypes(mapped);
      } catch (err) {
        console.error("Error fetching document types:", err);
        setDocumentTypes([]);
      } finally {
        setLoadingDocumentTypes(false);
      }
    };
    fetchDocumentTypes();
  }, [type]);

  // Handle attachment file change
  const handleAttachmentFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAttachmentForm(prev => ({ ...prev, file }));
    }
  };

  // Handle attachment form input change
  const handleAttachmentInputChange = (e) => {
    const { name, value } = e.target;
    setAttachmentForm(prev => ({ ...prev, [name]: value }));
  };

  // Handle edit attachment click
  const handleEditAttachment = (attachment) => {
    setEditingAttachment(attachment);
    const existingTypeId =
      attachment.Document_Type_Id ??
      attachment.document_type_id ??
      attachment.DocumentTypeId ??
      attachment.documentTypeId ??
      "";
    const existingTypeName = attachment.Document_Type || attachment.document_type || "";
    const inferredId =
      existingTypeId ||
      (existingTypeName
        ? (documentTypes.find((d) => d.name === existingTypeName)?.id ?? "")
        : "");
    setAttachmentForm({
      file: null, // Don't pre-fill file, user can choose to keep existing or upload new
      documentTypeId: inferredId ? String(inferredId) : "",
      remarks: attachment.Remarks || ""
    });
    setShowAttachmentModal(true);
  };

  // Handle close modal
  const handleCloseAttachmentModal = () => {
    setShowAttachmentModal(false);
    setEditingAttachment(null);
    setAttachmentForm({
      file: null,
      documentTypeId: "",
      remarks: ""
    });
  };

  // Delete attachment
  const handleDeleteAttachment = async (attachmentId) => {
    if (!(await confirm({
      title: "Delete Attachment",
      message: "Are you sure you want to delete this attachment? This action cannot be undone.",
    }))) {
      return;
    }

    try {
      const response = await axiosInstance.delete(`/attachment/${attachmentId}`);
      if (response.data && response.data.message) {
        alert(response.data.message || "Attachment deleted successfully");
        // Refresh attachments list
        fetchAttachments();
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || "Failed to delete attachment.";
      alert(errorMsg);
    }
  };

  // Upload or update attachment
  const handleUploadAttachment = async (e) => {
    e.preventDefault();
    
    // For new uploads, file is required. For updates, file is optional (can keep existing)
    if (!editingAttachment && !attachmentForm.file) {
      alert("Please select a file to upload.");
      return;
    }

    if (!attachmentForm.documentTypeId) {
      alert("Please select a document type.");
      return;
    }

    // Get logged-in user id from localStorage
    let userId = 1;
    try {
      const userStr = getAuthItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        userId = user.user_id || user.id || 1;
      }
    } catch (error) {
      console.error("Error reading user data:", error);
    }

    setUploadingAttachment(true);
    try {
      // Create FormData for multipart/form-data upload/update
      const formData = new FormData();
      
      // Only append file if a new file is selected (for updates, file is optional)
      if (attachmentForm.file) {
        formData.append('file', attachmentForm.file);
      }
      
      const selectedDoc = documentTypes.find(
        (d) => String(d.id) === String(attachmentForm.documentTypeId)
      );
      // Prefer sending id; also include name for backward compatibility
      formData.append('documentTypeId', String(attachmentForm.documentTypeId));
      if (selectedDoc?.name) {
        formData.append('documentType', selectedDoc.name);
      }
      formData.append('remarks', attachmentForm.remarks || '');
      
      if (editingAttachment) {
        // Update: use updatedById
        formData.append('updatedById', userId);
        
        // Send appropriate ID based on type
        if (type === "client") {
          formData.append('clientId', leadId);
        } else {
          formData.append('leadId', leadId);
        }

        // Make PUT request to update attachment
        const attachmentId = editingAttachment.Attachment_id || editingAttachment.Attachment_Id;
        const response = await axiosInstance.put(`/update-attachment/${attachmentId}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data && response.data.message) {
          alert(response.data.message || "Attachment updated successfully");
          handleCloseAttachmentModal();
          fetchAttachments();
        }
      } else {
        // Upload: use uploadedById
        formData.append('uploadedById', userId);
        
        // Send appropriate ID based on type
        if (type === "client") {
          formData.append('clientId', leadId);
        } else {
          formData.append('leadId', leadId);
        }

        // Make POST request to upload new attachment
        const response = await axiosInstance.post('/upload-attachment', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        if (response.data && response.data.message) {
          alert(response.data.message || "Attachment uploaded successfully");
          handleCloseAttachmentModal();
          fetchAttachments();
        }
      }
    } catch (error) {
      console.error(`Error ${editingAttachment ? 'updating' : 'uploading'} attachment:`, error);
      const errorMsg = error.response?.data?.error || error.response?.data?.message || `Failed to ${editingAttachment ? 'update' : 'upload'} attachment.`;
      alert(errorMsg);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  };

  const formatDateOnly = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "—";
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Filter followups
  const filteredFollowups = followups.filter(f => {
    if (followupSearch) {
      const search = followupSearch.toLowerCase();
      const remarksMatch = (f.Remarks || f.remarks || "").toLowerCase().includes(search);
      const typeMatch =
        (f.Followup_Type || f.Type || f.followup_type || "").toLowerCase().includes(search);
      if (!remarksMatch && !typeMatch) return false;
    }
    if (followupDate) {
      const fDate = f.Followup_Date ? new Date(f.Followup_Date).toISOString().split('T')[0] : '';
      if (fDate !== followupDate) return false;
    }
    return true;
  });

  // Filter attachments
  const filteredAttachments = attachments.filter(a => {
    if (attachmentSearch) {
      const search = attachmentSearch.toLowerCase();
      const fileName = a.File_Name || a.File_Path?.split('/').pop() || '';
      const docType = a.Document_Type || a.File_Type || '';
      const remarks = a.Remarks || '';
      if (!fileName.toLowerCase().includes(search) && 
          !docType.toLowerCase().includes(search) &&
          !remarks.toLowerCase().includes(search)) {
        return false;
      }
    }
    return true;
  });

  const getFollowupSortValue = (f, columnId) => {
    if (columnId === "followupDate") {
      const t = new Date(f.Followup_Date || f.followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "type") return String(f.Followup_Type || f.Type || f.followup_type || "").toLowerCase();
    if (columnId === "remarks") return String(f.Remarks || f.remarks || "").toLowerCase();
    if (columnId === "nextFollowupDate") {
      const t = new Date(f.Next_Followup_Date || f.next_followup_date || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    if (columnId === "createdAt") {
      const t = new Date(f.Created_At || f.Created_On || f.created_at || f.created_on || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    return "";
  };

  const sortedFollowups = useMemo(() => {
    const column = followupSortColumn || "nextFollowupDate";
    const direction = followupSortColumn ? followupSortDirection : "desc";
    const mult = direction === "desc" ? -1 : 1;
    return [...filteredFollowups].sort((a, b) => {
      const va = getFollowupSortValue(a, column);
      const vb = getFollowupSortValue(b, column);
      // Empty next-followup dates sink to the bottom when sorting desc
      if (column === "nextFollowupDate") {
        const aEmpty = !va;
        const bEmpty = !vb;
        if (aEmpty && !bEmpty) return 1;
        if (!aEmpty && bEmpty) return -1;
      }
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Followup_Id ?? a?.followup_id ?? a?.id ?? 0);
      const idb = Number(b?.Followup_Id ?? b?.followup_id ?? b?.id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredFollowups, followupSortColumn, followupSortDirection]);

  const followupTotalPages = Math.max(1, Math.ceil(sortedFollowups.length / followupRowsPerPage));
  const followupStartIdx = (followupPage - 1) * followupRowsPerPage;
  const currentFollowups = sortedFollowups.slice(followupStartIdx, followupStartIdx + followupRowsPerPage);

  useEffect(() => {
    if (followupPage > followupTotalPages && followupTotalPages > 0) {
      setFollowupPage(1);
    }
  }, [followupPage, followupTotalPages, sortedFollowups.length]);

  const handleFollowupRowsPerPageChange = (next) => {
    setFollowupRowsPerPage(next);
    setFollowupPage(1);
    localStorage.setItem(LEAD_FOLLOWUPS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleFollowupSort = (columnId) => {
    setFollowupPage(1);
    if (followupSortColumn === columnId) {
      setFollowupSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setFollowupSortColumn(columnId);
      setFollowupSortDirection("asc");
    }
  };

  const getAttachmentSortValue = (a, columnId) => {
    if (columnId === "fileName") {
      return String(a.File_Name || a.File_Path?.split("/").pop() || "").toLowerCase();
    }
    if (columnId === "documentType") return String(a.Document_Type || a.File_Type || "").toLowerCase();
    if (columnId === "remarks") return String(a.Remarks || "").toLowerCase();
    if (columnId === "uploadedAt") {
      const t = new Date(a.Uploaded_On || a.Created_At || a.Uploaded_At || "").getTime();
      return Number.isFinite(t) ? t : 0;
    }
    return "";
  };

  const sortedAttachments = useMemo(() => {
    if (!attachmentSortColumn) return filteredAttachments;
    const mult = attachmentSortDirection === "desc" ? -1 : 1;
    return [...filteredAttachments].sort((a, b) => {
      const va = getAttachmentSortValue(a, attachmentSortColumn);
      const vb = getAttachmentSortValue(b, attachmentSortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.Attachment_id ?? a?.Attachment_Id ?? 0);
      const idb = Number(b?.Attachment_id ?? b?.Attachment_Id ?? 0);
      return (ida - idb) * mult;
    });
  }, [filteredAttachments, attachmentSortColumn, attachmentSortDirection]);

  const attachmentTotalPages = Math.max(1, Math.ceil(sortedAttachments.length / attachmentRowsPerPage));
  const attachmentStartIdx = (attachmentPage - 1) * attachmentRowsPerPage;
  const currentAttachments = sortedAttachments.slice(attachmentStartIdx, attachmentStartIdx + attachmentRowsPerPage);

  useEffect(() => {
    if (attachmentPage > attachmentTotalPages && attachmentTotalPages > 0) {
      setAttachmentPage(1);
    }
  }, [attachmentPage, attachmentTotalPages, sortedAttachments.length]);

  const handleAttachmentRowsPerPageChange = (next) => {
    setAttachmentRowsPerPage(next);
    setAttachmentPage(1);
    localStorage.setItem(LEAD_ATTACHMENTS_ROWS_PER_PAGE_KEY, String(next));
  };

  const handleAttachmentSort = (columnId) => {
    setAttachmentPage(1);
    if (attachmentSortColumn === columnId) {
      setAttachmentSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setAttachmentSortColumn(columnId);
      setAttachmentSortDirection("asc");
    }
  };

  if (loading) {
    return (
      <div className="viewlead-container">
        <div className="viewlead-loading">Loading lead details...</div>
      </div>
    );
  }

  if (!leadData) {
    const isTrainingLead = type === "training-lead";
    return (
      <div className="viewlead-container">
        <button className="viewlead-back-link" onClick={onBack}>
          <FaArrowLeft /> Back to {type === "client" 
            ? "Clients" 
            : isTrainingLead 
            ? "Training Leads" 
            : "Leads"}
        </button>
        <div className="viewlead-loading">Lead not found.</div>
      </div>
    );
  }

  const isTrainingLead = type === "training-lead";
  const dataKey = type === "client" 
    ? "client" 
    : isTrainingLead 
    ? "training_lead" 
    : "lead";
  const lead = leadData[dataKey] || {};
  const address = leadData.address || {};
  const companyAddress = leadData.company_address || {};
  const preferCompanyAddr = !!(
    companyAddress.Address_Line_1 ||
    companyAddress.City_Id ||
    companyAddress.Zip_Code
  );
  const displayAddress = preferCompanyAddr ? companyAddress : address;
  const trainingCourses = isTrainingLead && Array.isArray(lead.Courses) ? lead.Courses : [];
  const trainingCourseLabel = isTrainingLead
    ? (trainingCourses.length > 0
        ? trainingCourses
            .map((c) => `${c.Course_Code || c.course_code || ""}${(c.Course_Code || c.course_code) && (c.Course_Name || c.course_name) ? " - " : ""}${c.Course_Name || c.course_name || ""}`.trim())
            .filter(Boolean)
            .join(", ")
        : (lead.Course_Name || lead.Course_Code || "—"))
    : null;
  const trainingModeLabel = isTrainingLead
    ? (() => {
        const arr = Array.isArray(lead.Modes) ? lead.Modes : [];
        const names = arr.map((m) => m.Mode_Name || m.mode_name).filter(Boolean);
        return names.length > 0 ? names.join(", ") : (lead.Mode || "—");
      })()
    : null;

  // Build address string (prefer company_address for tenants; same fields for others)
  const stateZip = [displayAddress.State_Name, displayAddress.Zip_Code].filter(Boolean).join(" - ");
  const addressLines = [
    displayAddress.Address_Line_1,
    displayAddress.Address_Line_2,
    displayAddress.Landmark,
    displayAddress.City_Name,
    stateZip,
    displayAddress.Country_Name
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean);

  const personalPhoneDisplay = (() => {
    const phone = lead.Phone || lead.phone || "";
    if (!phone) return "";
    const cc = lead.Country_Code || lead.country_code;
    const combined = cc
      ? `${String(cc).startsWith("+") ? cc : `+${cc}`}${String(phone).replace(/\D/g, "")}`
      : phone;
    return formatPhone(combined) || "";
  })();

  const altPhoneDisplay = (() => {
    const phone = lead.Alt_Phone || lead.alt_phone || "";
    if (!phone) return "";
    const cc = lead.Country_Code || lead.country_code;
    const combined = cc
      ? `${String(cc).startsWith("+") ? cc : `+${cc}`}${String(phone).replace(/\D/g, "")}`
      : phone;
    return formatPhone(combined) || "";
  })();

  const companyPhoneDisplay = (() => {
    const phone = lead.Company_Phone || lead.company_phone || "";
    if (!phone) return "";
    if (String(phone).startsWith("+") || String(phone).replace(/\D/g, "").length > 10) {
      return formatPhone(phone) || "";
    }
    const cc = lead.Country_Code || lead.country_code;
    const combined = cc
      ? `${String(cc).startsWith("+") ? cc : `+${cc}`}${String(phone).replace(/\D/g, "")}`
      : phone;
    return formatPhone(combined) || "";
  })();

  const getDomainDisplay = () => {
    // Prefer explicit name fields if present
    const nameField =
      lead.Domain ||
      lead.domain ||
      lead.Domain_Name ||
      lead.domain_name;
    if (nameField && String(nameField).trim() !== "") {
      return nameField;
    }
    const id = lead.Domain_Id ?? lead.domain_id;
    if (!id || domains.length === 0) return "";
    const dom = domains.find(
      (d) => d.id === id || String(d.id) === String(id)
    );
    return dom?.name || "";
  };

  const getCommunicationLanguage = () => {
    return (
      lead.Com_Lang ||
      lead.com_lang ||
      lead.Communication_Language ||
      lead.communicationLanguage ||
      lead.communication_language ||
      ""
    );
  };

  const InfoItem = ({ label, value, href, tone, children, className = "" }) => {
    if (children != null) {
      return (
        <div className={`viewlead-info-item ${className}`}>
          <span className="viewlead-info-label">{label}</span>
          <div className="viewlead-info-value">{children}</div>
        </div>
      );
    }
    const hasValue = value != null && String(value).trim() !== "" && value !== "—";
    const display = hasValue ? value : "—";
    return (
      <div
        className={`viewlead-info-item ${className} ${!hasValue ? "viewlead-info-item--empty" : ""}`}
      >
        <span className="viewlead-info-label">{label}</span>
        {hasValue && href ? (
          <a
            className="viewlead-info-value viewlead-link"
            href={href}
            onClick={(e) => e.stopPropagation()}
          >
            {display}
          </a>
        ) : tone && hasValue ? (
          <span className={`viewlead-info-value viewlead-status-pill viewlead-status-pill--${tone}`}>
            {display}
          </span>
        ) : (
          <span className={`viewlead-info-value ${!hasValue ? "muted" : ""}`}>
            {display}
          </span>
        )}
      </div>
    );
  };

  const handleAssignedToChange = async (userId) => {
    if (type === "client" || !leadId) return;
    const endpoint =
      type === "training-lead"
        ? `/update-training-lead/${leadId}`
        : `/update-lead/${leadId}`;
    setSavingAssigned(true);
    try {
      const payload = {
        Assigned_To: userId === "" || userId == null ? null : Number(userId),
      };
      const res = await axiosInstance.put(endpoint, payload);
      const key = type === "training-lead" ? "training_lead" : "lead";
      const updated = res.data?.[key] || res.data;
      if (updated && typeof updated === "object") {
        setLeadData((prev) => ({
          ...(prev || {}),
          [key]: { ...(prev?.[key] || {}), ...updated, Assigned_To: payload.Assigned_To, Assigned_To_Name: updated.Assigned_To_Name },
          address: prev?.address || {},
        }));
      } else {
        setLeadData((prev) => ({
          ...(prev || {}),
          [key]: { ...(prev?.[key] || {}), Assigned_To: payload.Assigned_To },
        }));
      }
    } catch (err) {
      alert(
        err.response?.data?.error ||
          err.response?.data?.message ||
          err.message ||
          "Failed to update Assigned To."
      );
    } finally {
      setSavingAssigned(false);
    }
  };

  // Company fields are shared between lead and tenant views but grouped into
  // different rows, so they are declared once and ordered per view below.
  const companyInfo = {
    name: <InfoItem label="Company Name" value={lead.Company_Name} />,
    business: <InfoItem label="Business" value={getDomainDisplay()} />,
    email: (
      <InfoItem
        label="Company Email"
        value={lead.Company_Email}
        href={lead.Company_Email ? `mailto:${lead.Company_Email}` : undefined}
      />
    ),
    phone: (
      <InfoItem
        label="Company Phone"
        value={companyPhoneDisplay || lead.Company_Phone}
        href={
          (companyPhoneDisplay || lead.Company_Phone)
            ? `tel:${String(companyPhoneDisplay || lead.Company_Phone).replace(/[^\d+]/g, "")}`
            : undefined
        }
      />
    ),
    website: <InfoItem label="Website" value={lead.Website} />,
    domain: (
      <InfoItem
        label="Domain Name"
        value={lead.Website_Domain_Name || lead.website_domain_name}
      />
    ),
    language: <InfoItem label="Communication Language" value={getCommunicationLanguage()} />,
    quoteSent: <InfoItem label="Is Quote Sent" value={lead.Is_Quote_Sent ? "Yes" : "No"} />,
    notes: (
      <InfoItem
        label="Notes"
        value={lead.Notes}
        className="viewlead-info-item--span2"
      />
    ),
    source: (
      <InfoItem
        label="Source"
        value={
          type === "client"
            ? (lead.Source || lead.Source_Name || "")
            : (lead.Source_Name || lead.Source || "")
        }
      />
    ),
    status: <InfoItem label="Status" value={lead.Status} tone={getStatusTone(lead.Status)} />,
    assignedTo: (
      <InfoItem label="Assigned To">
        <AssignedToSelect
          value={lead.Assigned_To ?? ""}
          users={assignUsers}
          disabled={savingAssigned}
          onChange={handleAssignedToChange}
        />
      </InfoItem>
    ),
  };

  const clientDetailTabs = [
    { id: "company", label: "Company" },
    { id: "address", label: "Address" },
    { id: "personal", label: "Personal" },
    { id: "services", label: "Opted Services" },
    { id: "social", label: "Social Accounts" },
    { id: "projects", label: "Projects" },
    { id: "invoices", label: "Invoices & Payments" },
    { id: "followups", label: "Followups" },
    { id: "attachments", label: "Attachments" },
  ];
  const showSection = (tabId) => type !== "client" || detailTab === tabId;
  const showInfoCard =
    type !== "client" ||
    detailTab === "personal" ||
    detailTab === "company" ||
    detailTab === "services" ||
    detailTab === "address";

  return (
    <div className={`viewlead-container${type === "client" ? " viewlead-container--tabs" : ""}`}>
      {/* Header (lead views only; tenants use tab-row actions) */}
      {type !== "client" && (
        <div className="viewlead-header">
          <button className="viewlead-back-link" onClick={onBack}>
            <FaArrowLeft /> Back to {isTrainingLead ? "Training Leads" : "Leads"}
          </button>
        </div>
      )}

      {type === "client" && (
        <div className="viewlead-detail-tabs-row">
          <div className="viewlead-detail-tabs" role="tablist" aria-label="Client sections">
            {clientDetailTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={detailTab === tab.id}
                className={`viewlead-detail-tab${detailTab === tab.id ? " active" : ""}`}
                onClick={() => setDetailTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <label className="viewlead-detail-tabs-select">
            <span className="viewlead-detail-tabs-select__label">Section</span>
            <select
              value={detailTab}
              onChange={(e) => setDetailTab(e.target.value)}
              aria-label="Client section"
            >
              {clientDetailTabs.map((tab) => (
                <option key={tab.id} value={tab.id}>
                  {tab.label}
                </option>
              ))}
            </select>
          </label>
          <div className="viewlead-detail-tabs-actions">
            <button type="button" className="btn-secondary-lead" onClick={onBack}>
              <FaArrowLeft /> Back to Clients
            </button>
            <button
              type="button"
              className="btn-edit-lead"
              onClick={() => onEditLead && onEditLead(leadId, type)}
            >
              Edit Client
            </button>
          </div>
        </div>
      )}

      {/* Lead/Customer Info Card */}
      {showInfoCard && (
      <div className="viewlead-card">
        {/* Personal Information */}
        {showSection("personal") && (
          <>
        <div className="viewlead-section-header viewlead-personal-section-header">
          <h3 className="viewlead-section-title no-border">Personal Information</h3>
          {type !== "client" && (
          <div className="viewlead-section-actions">
            <button className="btn-edit-lead" onClick={() => onEditLead && onEditLead(leadId, type)}>
              Edit {isTrainingLead 
                ? "Training Lead" 
                : "Lead"}
            </button>
          </div>
          )}
        </div>
        <div className="viewlead-info-grid">
          {type === "client" ? (
            <InfoItem
              label="Full Name"
              value={formatFullName(null, lead.First_Name, lead.Last_Name)}
            />
          ) : (
            <div className="viewlead-info-item viewlead-info-item--name-status">
              <span className="viewlead-info-label">Full Name</span>
              <div className="viewlead-name-status-row">
                {(() => {
                  const fullName = formatFullName(null, lead.First_Name, lead.Last_Name);
                  return (
                    <span className={`viewlead-info-value ${!fullName || fullName === "—" ? "muted" : ""}`}>
                      {fullName && fullName !== "—" ? fullName : "—"}
                    </span>
                  );
                })()}
                {lead.Status ? (
                  <span
                    className={`viewlead-status-pill viewlead-status-pill--${getStatusTone(
                      lead.Status
                    )}`}
                  >
                    {lead.Status}
                  </span>
                ) : null}
              </div>
            </div>
          )}
          <InfoItem
            label="Personal Email"
            value={lead.Email}
            href={lead.Email ? `mailto:${lead.Email}` : undefined}
          />
          <InfoItem
            label="Personal Phone"
            value={personalPhoneDisplay || lead.Phone}
            href={
              (personalPhoneDisplay || lead.Phone)
                ? `tel:${String(personalPhoneDisplay || lead.Phone).replace(/[^\d+]/g, "")}`
                : undefined
            }
          />
          {type === "client" ? (
            <>
              <InfoItem label="Alternate phone" value={altPhoneDisplay || lead.Alt_Phone} />
              <InfoItem label="Aadhar Number" value={lead.Aadhar} />
              <InfoItem
                label="Client type"
                value={lead.Customer_Type || lead.customer_type || "—"}
              />
              <InfoItem label="Owner Login Email" value={lead.Owner_Company_Email} />
              <InfoItem
                label="Portal"
                value={lead.Portal_Host ? `${lead.Portal_Host} (${portalTypeLabel(lead.Portal_Type)})` : null}
                href={lead.Portal_URL}
              />
            </>
          ) : null}
        </div>
        {type === "client" && ownerBusinesses.length > 0 ? (
          <div className="viewlead-owner-biz-links">
            <div className="viewlead-owner-biz-links__head">
              <span className="viewlead-info-label">Other companies of this owner</span>
              <span className="viewlead-owner-biz-primary">
                {companyLogin ? (
                  <span className="viewlead-owner-biz-login" title={`Linked to ${companyLogin.mainLogin || "main login"}`}>
                    Own login: {companyLogin.login}
                  </span>
                ) : isPrimaryCompany ? (
                  <span className="primary-badge">Primary: this company</span>
                ) : (
                  <button
                    type="button"
                    className="viewlead-owner-biz-make-primary"
                    onClick={handleMakePrimary}
                    disabled={settingPrimary}
                  >
                    {settingPrimary ? "Saving..." : "Make this primary"}
                  </button>
                )}
              </span>
            </div>
            <ul className="viewlead-owner-biz-links__list">
              {ownerBusinesses.map((b) => (
                <li key={b.Tenant_Id}>
                  <button
                    type="button"
                    className="viewlead-owner-biz-link"
                    onClick={() => navigate(`/tenants/${b.Tenant_Id}/view`)}
                  >
                    {b.Company_Name || "—"}
                  </button>
                  {b.Is_Primary ? <span className="primary-badge">Primary</span> : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
          </>
        )}

        {/* Company Information / Training Information */}
        {showSection("company") && (
          <>
        <h3 className="viewlead-section-title">{isTrainingLead ? "Training Information" : "Company Information"}</h3>
        <div className="viewlead-info-grid">
          {isTrainingLead ? (
            <>
              <InfoItem label="College Name" value={lead.College_Name} />
              <InfoItem label="Course" value={trainingCourseLabel} />
              <InfoItem
                label="Qualification"
                value={
                  (String(lead.Qualification_Id) === "15" || String(lead.qualification_id) === "15")
                    ? (lead.Other_Qualification || lead.other_qualification || lead.Qualification_Name || lead.qualification_name)
                    : (lead.Qualification_Name || lead.qualification_name)
                }
              />
              <InfoItem label="Year Of Passing" value={lead.Year_Of_Passing} />
              <InfoItem label="Batch" value={lead.Batch} />
              <InfoItem label="Mode" value={trainingModeLabel} />
              <InfoItem label="Expected Fee" value={lead.Expected_Fee} />
              {companyInfo.source}
              {companyInfo.assignedTo}
            </>
          ) : type === "client" ? (
            <>
              {companyInfo.name}
              {companyInfo.business}
              {companyInfo.status}
              {companyInfo.email}
              {companyInfo.phone}
              {companyInfo.website}
              {companyInfo.domain}
              {companyInfo.language}
              {companyInfo.source}
              {companyInfo.quoteSent}
              {companyInfo.notes}
            </>
          ) : (
            <>
              {companyInfo.name}
              {companyInfo.business}
              {companyInfo.email}
              {companyInfo.phone}
              {companyInfo.website}
              {companyInfo.language}
              {companyInfo.quoteSent}
              {companyInfo.source}
              {companyInfo.assignedTo}
            </>
          )}
        </div>
          </>
        )}

        {/* Services / Course */}
        {showSection("services") && (
          <>
        <h3 className="viewlead-section-title">
          {isTrainingLead ? "Course" : type === "client" ? "Opted Services" : "Services"}
        </h3>
        {isTrainingLead ? (
          (trainingCourses.length > 0 || lead.Course_Name) ? (
            <div className="viewlead-services-list">
              {trainingCourses.length > 0 ? (
                trainingCourses.map((c, idx) => (
                  <span key={idx} className="viewlead-service-badge">
                    {(c.Course_Code || c.course_code) ? `${c.Course_Code || c.course_code} - ` : ""}{c.Course_Name || c.course_name}
                  </span>
                ))
              ) : (
                <span className="viewlead-service-badge">
                  {(lead.Course_Code ? `${lead.Course_Code} - ` : "")}{lead.Course_Name}
                </span>
              )}
            </div>
          ) : (
            <p className="viewlead-no-data">No course assigned.</p>
          )
        ) : (
          Array.isArray(lead.Service_Groups) && lead.Service_Groups.length > 0 ? (
            <div className="viewlead-service-groups">
              {lead.Service_Groups.map((group) => (
                <div key={group.Category_Id ?? group.Category_Name} className="viewlead-service-group">
                  <div className="viewlead-service-group__head">
                    <span className="viewlead-service-group__name">{group.Category_Name}</span>
                    <span
                      className="viewlead-service-group__count"
                      title={`${group.Subcategories.length} selected`}
                    >
                      {group.Subcategories.length}
                    </span>
                  </div>
                  <div className="viewlead-services-list">
                    {group.Subcategories.map((sub) => (
                      <span key={sub.Subcategory_Id} className="viewlead-service-badge" title={sub.Description || undefined}>
                        {sub.Subcategory_Name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : lead.Services && lead.Services.length > 0 ? (
            <div className="viewlead-services-list">
              {lead.Services.map((service, idx) => (
                <span key={idx} className="viewlead-service-badge">{service}</span>
              ))}
            </div>
          ) : (
            <p className="viewlead-no-data">No services assigned.</p>
          )
        )}
          </>
        )}

        {/* Address */}
        {showSection("address") && (
          <>
        <h3 className="viewlead-section-title">{type === "client" ? "Company Address" : "Address"}</h3>
        {addressLines.length > 0 ? (
          <>
            <div className="viewlead-info-grid viewlead-address-grid">
              <InfoItem label="Address Line 1" value={displayAddress.Address_Line_1} />
              <InfoItem label="Address Line 2" value={displayAddress.Address_Line_2} />
              <InfoItem label="Landmark" value={displayAddress.Landmark} />
              <InfoItem label="City" value={displayAddress.City_Name} />
              <InfoItem label="State" value={displayAddress.State_Name} />
              <InfoItem label="Pincode" value={displayAddress.Zip_Code} />
              <InfoItem label="Country" value={displayAddress.Country_Name} />
            </div>
            <MapLink parts={addressLines} className="viewlead-map-link viewlead-address-map" />
          </>
        ) : (
          <p className="viewlead-address-text">—</p>
        )}
          </>
        )}
      </div>
      )}

      {/* Social Accounts Section (only for customers) */}
      {type === "client" && showSection("social") && (
        <div className="viewlead-section">
          <div className="viewlead-section-header">
            <h3>Social Accounts</h3>
            <div className="viewlead-section-actions">
              <button
                className="btn-add-followup"
                onClick={() => {
                  const client = leadData?.client || lead || {};
                  const typeRaw = String(client.Customer_Type || client.customer_type || "").trim().toLowerCase();
                  const isOrg =
                    typeRaw === "organization" ||
                    typeRaw === "organisation" ||
                    typeRaw === "org";
                  const company = String(client.Company_Name || client.company_name || "").trim();
                  const person = formatFullName(null, client.First_Name, client.Last_Name);
                  const personOk = person && person !== "—";
                  const displayName = formatPascal(
                    isOrg
                      ? company || (personOk ? person : "") || "Client"
                      : personOk
                        ? person
                        : company || "Client"
                  );
                  const id = client.client_id ?? client.Client_Id ?? leadId;
                  const status = client.Status || client.status || "";
                  navigate(`/tenants/${leadId}/social-accounts/add`, {
                    state: {
                      returnTo: `/tenants/${leadId}/view`,
                      clientId: leadId,
                      tenantTitle: {
                        name: displayName,
                        id,
                        status,
                      },
                    },
                  });
                }}
                type="button"
              >
                Add Account
              </button>
            </div>
          </div>
          {loadingAccounts ? (
            <p className="viewlead-no-data">Loading accounts...</p>
          ) : (
            <>
            <div className="viewlead-socialaccounts-table-wrap co-desktop-table">
            <table className="viewlead-table socialaccounts-table-frozen">
              <thead>
                <tr>
                  <th className="col-sticky col-0 viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(0), width: getSocialAccountsColumnWidth(0), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="accountName" label="Account Name" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 0)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(1), width: getSocialAccountsColumnWidth(1), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="username" label="Username" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 1)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(2), width: getSocialAccountsColumnWidth(2), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="password" label="Password" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 2)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(3), width: getSocialAccountsColumnWidth(3), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="platform" label="Platform" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 3)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(4), width: getSocialAccountsColumnWidth(4), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="profileUrl" label="Profile URL" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 4)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(5), width: getSocialAccountsColumnWidth(5), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="contact" label="Contact" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 5)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(6), width: getSocialAccountsColumnWidth(6), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="emailUsed" label="Email Used" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 6)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(7), width: getSocialAccountsColumnWidth(7), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="status" label="Status" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 7)}></span></th>
                  <th className="viewlead-th-sortable" style={{ minWidth: getSocialAccountsColumnMinWidth(8), width: getSocialAccountsColumnWidth(8), position: "relative" }}><div className="viewlead-th-sort-row"><SupportSortableHeader columnId="notes" label="Notes" sortColumn={socialSortColumn} sortDirection={socialSortDirection} onSort={handleSocialSort} /></div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 8)}></span></th>
                  <th className="col-sticky col-action" style={{ minWidth: getSocialAccountsColumnMinWidth(9), width: getSocialAccountsColumnWidth(9), position: "relative" }}><div style={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>Actions</div><span className="column-resizer" onMouseDown={(e) => handleSocialAccountsResizeStart(e, 9)}></span></th>
                </tr>
              </thead>
              <tbody>
                {currentSocialAccounts.length > 0 ? (
                  currentSocialAccounts.map((account) => (
                  <tr key={account.id || account.account_id}>
                    <td className="col-sticky col-0" style={{ minWidth: getSocialAccountsColumnMinWidth(0), width: getSocialAccountsColumnWidth(0) }}>
                      {account.id ? (
                        <span
                          className="table-name-link"
                          onClick={() =>
                            navigate(`/tenants/${leadId}/social-accounts/${account.id}/view`, {
                              state: { returnTo: `/tenants/${leadId}/view` }
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              navigate(`/tenants/${leadId}/social-accounts/${account.id}/view`, {
                                state: { returnTo: `/tenants/${leadId}/view` }
                              });
                            }
                          }}
                          role="button"
                          tabIndex={0}
                          style={{ cursor: "pointer", color: "#2977ed" }}
                        >
                          {account.accountName || "—"}
                        </span>
                      ) : (
                        account.accountName || "—"
                      )}
                    </td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(1), width: getSocialAccountsColumnWidth(1) }}>{account.username || "—"}</td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(2), width: getSocialAccountsColumnWidth(2), fontFamily: "monospace" }}>{account.password || "—"}</td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(3), width: getSocialAccountsColumnWidth(3) }}>{account.platform || "—"}</td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(4), width: getSocialAccountsColumnWidth(4) }}>
                      {account.profileUrl && account.profileUrl !== "—" ? (
                        <a 
                          href={account.profileUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          style={{ color: "#2977ed", textDecoration: "none" }}
                        >
                          {account.profileUrl}
                        </a>
                      ) : "—"}
                    </td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(5), width: getSocialAccountsColumnWidth(5) }}>{account.contact || "—"}</td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(6), width: getSocialAccountsColumnWidth(6) }}>{account.emailUsed || "—"}</td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(7), width: getSocialAccountsColumnWidth(7) }}>
                      <span 
                        className={`status-badge ${(account.status || "").toLowerCase() === "active" ? "status-active" : "status-inactive"}`}
                      >
                        {account.status || "Inactive"}
                      </span>
                    </td>
                    <td style={{ minWidth: getSocialAccountsColumnMinWidth(8), width: getSocialAccountsColumnWidth(8) }}>{account.notes || "—"}</td>
                    <td className="col-sticky col-action" style={{ minWidth: getSocialAccountsColumnMinWidth(9), width: getSocialAccountsColumnWidth(9) }}>
                      <button
                        type="button"
                        className="btn-edit-lead"
                        style={{ padding: "6px 10px", fontSize: 12 }}
                        onClick={() =>
                          navigate(`/tenants/${leadId}/social-accounts/${account.id}/edit`, {
                            state: { returnTo: `/tenants/${leadId}/view` }
                          })
                        }
                        disabled={!account.id}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        type="button"
                        className="btn-edit-lead"
                        style={{
                          padding: "6px 10px",
                          fontSize: 12,
                          marginLeft: 8,
                          color: "#DC2626",
                          borderColor: "#FCA5A5"
                        }}
                        onClick={() => handleDeleteCustomerSocialAccount(account.id || account.account_id)}
                        disabled={!account.id && !account.account_id}
                        title="Delete"
                      >
                        <FaTrashAlt />
                      </button>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={10} className="viewlead-no-data">
                      No social accounts found for this client.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            <ResultsMobileList
              emptyLabel="No social accounts found for this client."
              className="viewlead-social-mobile"
            >
              {currentSocialAccounts.map((account) => {
                const accountId = account.id || account.account_id;
                const status = account.status || "Inactive";
                const statusTone =
                  String(status).toLowerCase() === "active" ? "success" : "neutral";
                return (
                  <ResultsMobileCard
                    key={accountId || account.accountName}
                    title={account.accountName || "—"}
                    subtitle={account.username || account.platform || null}
                    meta={[{ label: status, tone: statusTone }]}
                    fields={[
                      { label: "Platform", value: account.platform },
                      { label: "Username", value: account.username },
                      { label: "Contact", value: account.contact },
                      { label: "Email", value: account.emailUsed },
                      {
                        label: "Profile",
                        value:
                          account.profileUrl && account.profileUrl !== "—"
                            ? account.profileUrl
                            : null,
                      },
                      { label: "Notes", value: account.notes },
                    ]}
                    onOpen={
                      accountId
                        ? () =>
                            navigate(`/tenants/${leadId}/social-accounts/${accountId}/view`, {
                              state: { returnTo: `/tenants/${leadId}/view` },
                            })
                        : undefined
                    }
                    actions={
                      <div className="co-mcard__action-grid">
                        {account.profileUrl && account.profileUrl !== "—" ? (
                          <a
                            className="co-maction"
                            href={account.profileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open profile"
                            aria-label="Open profile"
                          >
                            <FaExternalLinkAlt size={13} />
                            <span>Open</span>
                          </a>
                        ) : null}
                        <button
                          type="button"
                          className="co-maction"
                          title="Edit"
                          aria-label="Edit"
                          disabled={!accountId}
                          onClick={() =>
                            navigate(`/tenants/${leadId}/social-accounts/${accountId}/edit`, {
                              state: { returnTo: `/tenants/${leadId}/view` },
                            })
                          }
                        >
                          <FaEdit size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          disabled={!accountId}
                          onClick={() => handleDeleteCustomerSocialAccount(accountId)}
                        >
                          <FaTrashAlt size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    }
                  />
                );
              })}
            </ResultsMobileList>
            <TablePagination
              currentPage={socialAccountPage}
              totalItems={sortedCustomerAccounts.length}
              rowsPerPage={socialAccountRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              onPageChange={setSocialAccountPage}
              onRowsPerPageChange={handleSocialAccountRowsPerPageChange}
              itemLabel="social accounts"
              rowsPerPageSelectId="customer-social-accounts-rows-per-page"
            />
            </>
          )}
        </div>
      )}

      {/* Projects Section (only for customers) */}
      {type === "client" && showSection("projects") && (
        <div className="viewlead-section">
          {loadingProjects ? (
            <p className="viewlead-no-data">Loading projects...</p>
          ) : (
            <>
            <div className="viewlead-projects-table-wrap co-desktop-table">
            <table className="viewlead-table viewlead-projects-table">
              <thead>
                <tr>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(0)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="projectName" label="Project Name" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(0, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(1)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="shortCode" label="Short Code" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(1, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(2)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="domain" label="Business" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(2, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(3)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="category" label="Category" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(3, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(4)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="status" label="Status" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(4, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(5)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="startDate" label="Start Date" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(5, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(6)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="endDate" label="End Date" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(6, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getProjectColStyle(7)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="visibility" label="Visibility" sortColumn={projectSortColumn} sortDirection={projectSortDirection} onSort={handleProjectSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleProjectResizeStart(7, e); }} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentCustomerProjects.length > 0 ? (
                  currentCustomerProjects.map((project) => (
                  <tr key={project.id}>
                    <td>
                      <span
                        className="table-name-link"
                        onClick={() => navigate(`/projects/${project.id}/view`)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate(`/projects/${project.id}/view`);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        style={{ cursor: "pointer", color: "#2977ed" }}
                      >
                        {project.projectName || "—"}
                      </span>
                    </td>
                    <td>{project.shortCode || "—"}</td>
                    <td>{project.domain || "—"}</td>
                    <td>{project.category || "—"}</td>
                    <td>
                      <span
                        className={`status-badge status-${(project.status || "")
                          .toLowerCase()
                          .replace(/\s/g, "-")}`}
                      >
                        {project.status || "—"}
                      </span>
                    </td>
                    <td>{formatDateOnly(project.startDate)}</td>
                    <td>{formatDateOnly(project.endDate)}</td>
                    <td>{project.visibility || "—"}</td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={8} className="viewlead-no-data">
                      {customerProjectsStatusPreset === "all"
                        ? "No projects found for this client."
                        : "No projects found for this client in selected status."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            <ResultsMobileList
              emptyLabel={
                customerProjectsStatusPreset === "all"
                  ? "No projects found for this client."
                  : "No projects found for this client in selected status."
              }
              className="viewlead-projects-mobile"
            >
              {currentCustomerProjects.map((project) => {
                const status = project.status || "—";
                const statusTone = getStatusTone(status);
                return (
                  <ResultsMobileCard
                    key={project.id}
                    title={project.projectName || "—"}
                    subtitle={project.shortCode || project.domain || null}
                    meta={[
                      { label: status, tone: statusTone },
                      ...(project.category
                        ? [{ label: project.category, tone: "info" }]
                        : []),
                    ]}
                    fields={[
                      { label: "Business", value: project.domain },
                      { label: "Start", value: formatDateOnly(project.startDate) },
                      { label: "End", value: formatDateOnly(project.endDate) },
                      { label: "Visibility", value: project.visibility },
                    ]}
                    onOpen={() => navigate(`/projects/${project.id}/view`)}
                    actions={
                      <div className="co-mcard__action-grid">
                        <button
                          type="button"
                          className="co-maction"
                          title="Open project"
                          aria-label="Open project"
                          onClick={() => navigate(`/projects/${project.id}/view`)}
                        >
                          <FaExternalLinkAlt size={13} />
                          <span>Open</span>
                        </button>
                      </div>
                    }
                  />
                );
              })}
            </ResultsMobileList>
            <TablePagination
              currentPage={projectPage}
              totalItems={sortedCustomerProjects.length}
              rowsPerPage={projectRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              onPageChange={setProjectPage}
              onRowsPerPageChange={handleProjectRowsPerPageChange}
              itemLabel="projects"
              rowsPerPageSelectId="customer-projects-rows-per-page"
            />
            </>
          )}
        </div>
      )}

      {type === "client" && showSection("invoices") && (
        <TenantInvoicesTab
          clientId={leadId}
          client={lead}
          addressStr={addressLines.length > 0 ? addressLines.join(", ") : null}
          services={lead.Services || []}
        />
      )}

      {/* Followups Section */}
      {showSection("followups") && (
      <div className="viewlead-section">
        <div className="viewlead-section-header">
          <h3>Followups</h3>
          <div className="viewlead-section-actions">
            <input
              type="text"
              className="viewlead-search-input"
              placeholder="Search by remarks or type..."
              value={followupSearch}
              onChange={(e) => {
                setFollowupSearch(e.target.value);
                setFollowupPage(1);
              }}
            />
            <input
              type="date"
              className="viewlead-date-input"
              value={followupDate}
              onChange={(e) => {
                setFollowupDate(e.target.value);
                setFollowupPage(1);
              }}
            />
            <button 
              className="btn-add-followup" 
              onClick={() => setShowAddFollowupModal(true)}
              title="New Followup"
              style={{ 
                display: "inline-flex", 
                alignItems: "center", 
                gap: "6px"
              }}
            >
              New Followup
            </button>
          </div>
        </div>
        <>
          <div className="viewlead-followups-table-wrap co-desktop-table">
          <table className="viewlead-table viewlead-followups-table">
            <thead>
              <tr>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(0)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="followupDate" label="Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(0, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(1)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="type" label="Type" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(1, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(2)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="remarks" label="Remarks" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(2, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(3)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="nextFollowupDate" label="Next Followup Date" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(3, e); }} />
                </th>
                <th className="viewlead-th-sortable" style={getFollowupColStyle(4)}>
                  <div className="viewlead-th-sort-row">
                    <SupportSortableHeader columnId="createdAt" label="Created At" sortColumn={followupSortColumn} sortDirection={followupSortDirection} onSort={handleFollowupSort} />
                  </div>
                  <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleFollowupResizeStart(4, e); }} />
                </th>
                <th style={{ ...getFollowupColStyle(5), textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentFollowups.length > 0 ? (
                currentFollowups.map((f, idx) => {
                  const followupId = f.Followup_Id || f.followup_id || f.id;
                  return (
                    <tr key={followupId || idx}>
                      <td>{formatDateTime(f.Followup_Date || f.followup_date)}</td>
                      <td>{f.Followup_Type || f.Type || f.followup_type || "—"}</td>
                      <td>{f.Remarks || f.remarks || "—"}</td>
                      <td className={!f.Next_Followup_Date && !f.next_followup_date ? "muted-cell" : ""}>
                        {f.Next_Followup_Date || f.next_followup_date ? formatDateTime(f.Next_Followup_Date || f.next_followup_date) : "—"}
                      </td>
                      <td className="blue-cell">{formatDateTime(f.Created_At || f.Created_On || f.created_at || f.created_on)}</td>
                      <td>
                        <div className="viewlead-followup-actions">
                          {followupId ? (
                            <>
                              <button
                                type="button"
                                className="btn-edit-followup"
                                onClick={() => handleEditFollowup(f)}
                                title="Edit followup"
                              >
                                <FaEdit />
                              </button>
                              <button 
                                className="btn-delete-followup" 
                                onClick={() => handleDeleteFollowup(followupId)}
                                title="Delete followup"
                              >
                                <FaTrashAlt />
                              </button>
                            </>
                          ) : (
                            <span className="muted-text">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="viewlead-no-data">
                    No followups found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
          <ResultsMobileList
            emptyLabel="No followups found."
            className="viewlead-followups-mobile"
          >
            {currentFollowups.map((f, idx) => {
              const followupId = f.Followup_Id || f.followup_id || f.id;
              const followupType = f.Followup_Type || f.Type || f.followup_type || "—";
              const remarks = f.Remarks || f.remarks || "";
              const nextDate = f.Next_Followup_Date || f.next_followup_date;
              return (
                <ResultsMobileCard
                  key={followupId || idx}
                  title={followupType}
                  subtitle={formatDateTime(f.Followup_Date || f.followup_date)}
                  meta={
                    nextDate
                      ? [{ label: "Next", tone: "warning" }]
                      : [{ label: "Done", tone: "success" }]
                  }
                  fields={[
                    { label: "Remarks", value: remarks },
                    {
                      label: "Next",
                      value: nextDate ? formatDateTime(nextDate) : null,
                    },
                    {
                      label: "Created",
                      value: formatDateTime(
                        f.Created_At || f.Created_On || f.created_at || f.created_on
                      ),
                    },
                  ]}
                  actions={
                    followupId ? (
                      <div className="co-mcard__action-grid">
                        <button
                          type="button"
                          className="co-maction"
                          title="Edit"
                          aria-label="Edit"
                          onClick={() => handleEditFollowup(f)}
                        >
                          <FaEdit size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => handleDeleteFollowup(followupId)}
                        >
                          <FaTrashAlt size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    ) : null
                  }
                />
              );
            })}
          </ResultsMobileList>
          <TablePagination
            currentPage={followupPage}
            totalItems={sortedFollowups.length}
            rowsPerPage={followupRowsPerPage}
            rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
            onPageChange={setFollowupPage}
            onRowsPerPageChange={handleFollowupRowsPerPageChange}
            itemLabel="followups"
            rowsPerPageSelectId="lead-followups-rows-per-page"
          />
        </>
      </div>
      )}

      {/* Add Followup Modal - popup on same page */}
      {showAddFollowupModal && (
        <div className="viewlead-modal-overlay viewlead-addfollowup-overlay" onClick={() => setShowAddFollowupModal(false)}>
          <div className="viewlead-modal-content viewlead-addfollowup-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">New Followup</h3>
              <button type="button" className="viewlead-modal-close" onClick={() => setShowAddFollowupModal(false)}>&times;</button>
            </div>
            <div className="viewlead-addfollowup-modal-body">
              <AddFollowup id={leadId} type={type} onBack={handleCloseAddFollowupModal} />
            </div>
          </div>
        </div>
      )}

      {/* Edit Followup Modal */}
      {editingFollowup && (
        <div className="viewlead-modal-overlay" onClick={() => !updatingFollowup && handleCloseEditFollowup()}>
          <div className="viewlead-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3 className="viewlead-modal-title">Edit Followup</h3>
              <button type="button" className="viewlead-modal-close" onClick={handleCloseEditFollowup} disabled={updatingFollowup}>&times;</button>
            </div>
            <form onSubmit={handleUpdateFollowupSubmit} className="viewlead-form-inner">
              <div className="viewlead-form-group">
                <label>Followup Date <span className="required">*</span></label>
                <input
                  type="datetime-local"
                  name="followupDate"
                  value={followupEditForm.followupDate}
                  onChange={handleFollowupEditChange}
                  required
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Followup Type</label>
                <input
                  type="text"
                  name="followupType"
                  value={followupEditForm.followupType}
                  onChange={handleFollowupEditChange}
                  placeholder="e.g., Call, Meeting, WhatsApp"
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={followupEditForm.remarks}
                  onChange={handleFollowupEditChange}
                  rows={3}
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-form-group">
                <label>Next Followup Date</label>
                <input
                  type="datetime-local"
                  name="nextFollowupDate"
                  value={followupEditForm.nextFollowupDate}
                  onChange={handleFollowupEditChange}
                  disabled={updatingFollowup}
                />
              </div>
              <div className="viewlead-modal-actions">
                <button type="submit" className="btn-submit" disabled={updatingFollowup}>
                  {updatingFollowup ? "Updating..." : "Update Followup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Attachments Section (hidden for training leads) */}
      {type !== "training-lead" && showSection("attachments") && (
        <div className="viewlead-section">
          <div className="viewlead-section-header">
            <h3>Attachments</h3>
            <div className="viewlead-section-actions">
              <input
                type="text"
                className="viewlead-search-input"
                placeholder="Search attachments by file name or type..."
                value={attachmentSearch}
                onChange={(e) => {
                  setAttachmentSearch(e.target.value);
                  setAttachmentPage(1);
                }}
              />
              <button 
                className="btn-add-attachment" 
                onClick={() => setShowAttachmentModal(true)}
                title="Add Attachment"
                style={{ 
                  display: "inline-flex", 
                  alignItems: "center", 
                  gap: "6px"
                }}
              >
                Add Attachment
              </button>
            </div>
          </div>
          <>
            <div className="viewlead-attachments-table-wrap co-desktop-table">
            <table className="viewlead-table viewlead-attachments-table">
              <thead>
                <tr>
                  <th className="viewlead-th-sortable" style={getAttachmentColStyle(0)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="fileName" label="File Name" sortColumn={attachmentSortColumn} sortDirection={attachmentSortDirection} onSort={handleAttachmentSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleAttachmentResizeStart(0, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getAttachmentColStyle(1)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="documentType" label="Document Type" sortColumn={attachmentSortColumn} sortDirection={attachmentSortDirection} onSort={handleAttachmentSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleAttachmentResizeStart(1, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getAttachmentColStyle(2)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="remarks" label="Remarks" sortColumn={attachmentSortColumn} sortDirection={attachmentSortDirection} onSort={handleAttachmentSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleAttachmentResizeStart(2, e); }} />
                  </th>
                  <th className="viewlead-th-sortable" style={getAttachmentColStyle(3)}>
                    <div className="viewlead-th-sort-row">
                      <SupportSortableHeader columnId="uploadedAt" label="Uploaded At" sortColumn={attachmentSortColumn} sortDirection={attachmentSortDirection} onSort={handleAttachmentSort} />
                    </div>
                    <span className="column-resizer" aria-hidden onMouseDown={(e) => { e.stopPropagation(); handleAttachmentResizeStart(3, e); }} />
                  </th>
                  <th style={getAttachmentColStyle(4)}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentAttachments.length > 0 ? (
                  currentAttachments.map((a, idx) => (
                  <tr key={a.Attachment_id || a.Attachment_Id || idx}>
                    <td>
                      {a.File_Url ? (
                        <a href={fixFileUrl(a.File_Url)} target="_blank" rel="noopener noreferrer" className="viewlead-file-link">
                          {a.File_Name || a.File_Path?.split('/').pop() || "—"}
                        </a>
                      ) : (
                        a.File_Name || a.File_Path?.split('/').pop() || "—"
                      )}
                    </td>
                    <td>{a.Document_Type || a.File_Type || "—"}</td>
                    <td>{a.Remarks || "—"}</td>
                    <td>{formatDateTime(a.Uploaded_On || a.Created_At || a.Uploaded_At)}</td>
                    <td>
                      <div className="viewlead-attachment-actions">
                        <button
                          className="btn-edit-attachment"
                          onClick={() => handleEditAttachment(a)}
                          title="Edit attachment"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="btn-delete-attachment"
                          onClick={() => handleDeleteAttachment(a.Attachment_id || a.Attachment_Id)}
                          title="Delete attachment"
                        >
                          <FaTrashAlt />
                        </button>
                      </div>
                    </td>
                  </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="viewlead-no-data">
                      No attachments. Click "Add Attachment" to add.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>
            <ResultsMobileList
              emptyLabel='No attachments. Click "Add Attachment" to add.'
              className="viewlead-attachments-mobile"
            >
              {currentAttachments.map((a, idx) => {
                const attachmentId = a.Attachment_id || a.Attachment_Id;
                const fileName = a.File_Name || a.File_Path?.split("/").pop() || "—";
                const docType = a.Document_Type || a.File_Type || null;
                const fileUrl = a.File_Url ? fixFileUrl(a.File_Url) : null;
                return (
                  <ResultsMobileCard
                    key={attachmentId || idx}
                    title={fileName}
                    subtitle={docType}
                    fields={[
                      { label: "Remarks", value: a.Remarks },
                      {
                        label: "Uploaded",
                        value: formatDateTime(
                          a.Uploaded_On || a.Created_At || a.Uploaded_At
                        ),
                      },
                    ]}
                    onOpen={
                      fileUrl
                        ? () => window.open(fileUrl, "_blank", "noopener,noreferrer")
                        : undefined
                    }
                    actions={
                      <div className="co-mcard__action-grid">
                        {fileUrl ? (
                          <a
                            className="co-maction"
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open file"
                            aria-label="Open file"
                          >
                            <FaExternalLinkAlt size={13} />
                            <span>Open</span>
                          </a>
                        ) : null}
                        <button
                          type="button"
                          className="co-maction"
                          title="Edit"
                          aria-label="Edit"
                          onClick={() => handleEditAttachment(a)}
                        >
                          <FaEdit size={14} />
                          <span>Edit</span>
                        </button>
                        <button
                          type="button"
                          className="co-maction co-maction--danger"
                          title="Delete"
                          aria-label="Delete"
                          onClick={() => handleDeleteAttachment(attachmentId)}
                        >
                          <FaTrashAlt size={14} />
                          <span>Delete</span>
                        </button>
                      </div>
                    }
                  />
                );
              })}
            </ResultsMobileList>
            <TablePagination
              currentPage={attachmentPage}
              totalItems={sortedAttachments.length}
              rowsPerPage={attachmentRowsPerPage}
              rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
              onPageChange={setAttachmentPage}
              onRowsPerPageChange={handleAttachmentRowsPerPageChange}
              itemLabel="attachments"
              rowsPerPageSelectId="lead-attachments-rows-per-page"
            />
          </>
        </div>
      )}

      {/* Attachment Upload/Edit Modal (hidden for training leads) */}
      {showAttachmentModal && type !== "training-lead" && (
        <div className="viewlead-modal-overlay" onClick={() => !uploadingAttachment && handleCloseAttachmentModal()}>
          <div className="viewlead-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="viewlead-modal-header">
              <h3>{editingAttachment ? "Edit Attachment" : "Upload Attachment"}</h3>
              <button className="viewlead-modal-close" onClick={handleCloseAttachmentModal} disabled={uploadingAttachment}>
                ×
              </button>
            </div>
            <form onSubmit={handleUploadAttachment} className="viewlead-attachment-form">
              <div className="viewlead-form-group">
                <label>File {!editingAttachment && <span className="required">*</span>}</label>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                  onChange={handleAttachmentFileChange}
                  disabled={uploadingAttachment}
                  required={!editingAttachment}
                />
                {attachmentForm.file && (
                  <p className="viewlead-file-name">{attachmentForm.file.name}</p>
                )}
                {editingAttachment && !attachmentForm.file && (
                  <p className="viewlead-file-info">
                    Current file: {editingAttachment.File_Path?.split('/').pop() || editingAttachment.File_Name || "—"}
                    <br />
                    <span style={{ fontSize: '12px', color: '#7A8CA5' }}>Leave empty to keep existing file</span>
                  </p>
                )}
              </div>
              <div className="viewlead-form-group">
                <label>Document Type <span className="required">*</span></label>
                <select
                  name="documentTypeId"
                  value={attachmentForm.documentTypeId}
                  onChange={handleAttachmentInputChange}
                  disabled={uploadingAttachment || loadingDocumentTypes}
                  required
                >
                  <option value="">
                    {loadingDocumentTypes ? "Loading document types..." : "Select Document Type"}
                  </option>
                  {documentTypes.map((dt) => (
                    <option key={dt.id} value={dt.id}>
                      {dt.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="viewlead-form-group">
                <label>Remarks</label>
                <textarea
                  name="remarks"
                  value={attachmentForm.remarks}
                  onChange={handleAttachmentInputChange}
                  placeholder="Enter remarks (optional)"
                  rows={4}
                  disabled={uploadingAttachment}
                />
              </div>
              <div className="viewlead-modal-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={handleCloseAttachmentModal}
                  disabled={uploadingAttachment}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={uploadingAttachment}
                >
                  {uploadingAttachment 
                    ? (editingAttachment ? "Updating..." : "Uploading...") 
                    : (editingAttachment ? "Update" : "Upload")
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
